import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import Button from '../../components/ui/Button';
import { addWorkflowStep } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

const DECISION_PAIRS = [
  { value: 'approve_reject', label: 'Approved / Rejected' },
  { value: 'pass_fail', label: 'Pass / Fail' },
];

export default function AddWorkflowStepModal({ open, onClose, category, availableDefinitions }) {
  const queryClient = useQueryClient();

  const [mode, setMode] = useState('existing');
  const [stepDefinitionId, setStepDefinitionId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('task');
  const [decisionPair, setDecisionPair] = useState('approve_reject');
  const [groupId, setGroupId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setMode('new');
      setStepDefinitionId(availableDefinitions?.[0]?.id ? String(availableDefinitions[0].id) : '');
      setTitle('');
      setDescription('');
      setKind('task');
      setDecisionPair('approve_reject');
      setGroupId('');
      setError('');
    }
  }, [open, availableDefinitions]);

  const mutation = useMutation({
    mutationFn: () =>
      addWorkflowStep(category.id, {
        step_definition_id: mode === 'existing' ? Number(stepDefinitionId) : undefined,
        title: mode === 'new' ? title : undefined,
        kind: mode === 'new' ? kind : undefined,
        description: mode === 'new' ? description || undefined : undefined,
        decision_pair: mode === 'new' && kind === 'decision' ? decisionPair : undefined,
        step_group_id: groupId ? Number(groupId) : undefined,
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step added.' });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.message || Object.values(err.response?.data?.errors || {})[0]?.[0] || 'Could not add step.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'existing' && !stepDefinitionId) {
      setError('Choose a step.');
      return;
    }
    if (mode === 'new' && !title.trim()) {
      setError('Enter a title.');
      return;
    }
    mutation.mutate();
  };

  if (!category) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add step to ${category.code}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Add step
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm">
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              mode === 'new' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Create a new step
          </button>
          <button
            type="button"
            onClick={() => availableDefinitions?.length && setMode('existing')}
            disabled={!availableDefinitions?.length}
            className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
              mode === 'existing' ? 'bg-primary text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            } ${!availableDefinitions?.length ? 'cursor-not-allowed opacity-40' : ''}`}
          >
            Reuse an existing step
          </button>
        </div>

        {mode === 'existing' && (
          <Select label="Step" value={stepDefinitionId} onChange={(e) => setStepDefinitionId(e.target.value)}>
            {(availableDefinitions || []).length === 0 && <option value="">No reusable steps left</option>}
            {(availableDefinitions || []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.title}
              </option>
            ))}
          </Select>
        )}

        {mode === 'new' && (
          <>
            <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <Textarea label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            <Select label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="task">Task</option>
              <option value="decision">Decision</option>
            </Select>
            {kind === 'decision' && (
              <Select label="Decision labels" value={decisionPair} onChange={(e) => setDecisionPair(e.target.value)}>
                {DECISION_PAIRS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </Select>
            )}
          </>
        )}

        <Select label="Group (optional)" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          <option value="">Ungrouped</option>
          {(category.groups || []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
}
